import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import clsx from "clsx";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { useDocumentListAPI } from "@/app/hooks/api_hooks/CertificateDocument/useDocumentListAPI";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { ROUTES } from "@/app/routes";
import { ICertificateState } from "@/app_redux/reducers/slices/auth/certificate_slice";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import PrintCertificate from "./PrintCertificate";
import UploadedDocuments from "./UploadedDocuments";

const CertificatesTab = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const sourceParam = searchParams.get("document_source");
  const [page_step, setPageStep] = useState<"print_certificate" | "upload_certificate">("print_certificate");
  const { setVisible: uploadDocument } = useModalActions("CERTIFICATE_DOCUMENT_UPLOAD_MODAL");
  const { routeState } = useRouteInfo(
    ROUTES.ROOT.children.CUSTOMER_PROFILE.children.DETAILS_CUSTOMER.children.CERTIFICATES_TAB.id,
  );
  const { documentList } = routeState?.state as ICertificateState;
  const { routeState: customerRouteState } = useRouteInfo(ROUTES.ROOT.children.CUSTOMER_PROFILE.id);
  const { customerId, petId } = customerRouteState?.state as ICustomerProfileState;
  const { execute: fetchDocumentList } = useDocumentListAPI();

  const tabs = useMemo(
    () => [
      { id: "print_certificate" as const, label: "Print Certificate" },
      { id: "upload_certificate" as const, label: "Uploaded Documents" },
    ],
    [],
  );

  useEffect(() => {
    if (customerId) fetchDocumentList(customerId);
  }, [customerId, fetchDocumentList]);

  const handleCertificateDocumentUploadClick = () => {
    uploadDocument();
  };

  useEffect(() => {
    setPageStep(sourceParam === "upload_certificate" ? "upload_certificate" : "print_certificate");
  }, [sourceParam]);

  const handleTabClick = useCallback(
    (source: "print_certificate" | "upload_certificate") => {
      if (!customerId || !petId) return;
      setSearchParams({ document_source: source }, { replace: true });
      navigate(
        `/app/customer/profile/customer-details/certificates/${customerId}/${petId}?document_source=${source}`,
      );
    },
    [customerId, petId, navigate, setSearchParams],
  );

  const isPrint = page_step === "print_certificate";
  const docCount = Number(documentList?.total_documents_count ?? 0);

  return (
    <div>
      <div className="flex flex-col gap-3 bg-white sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div className="flex gap-6">
          {tabs.map((tab) => {
            const isActive = page_step === tab.id;
            const showCount = tab.id === "upload_certificate" && docCount > 0;
            return (
              <button
                key={tab.id}
                className={clsx(
                  "flex items-center gap-2 whitespace-nowrap border-b-2 pb-2 text-xs transition-colors focus:outline-none",
                  isActive
                    ? "border-indigo-600 font-semibold text-indigo-600"
                    : "border-transparent font-normal text-gray-600 hover:text-gray-900",
                )}
                onClick={() => handleTabClick(tab.id)}
                type="button"
              >
                {tab.label}
                {showCount && (
                  <span
                    className={clsx(
                      "inline-flex items-center rounded-full px-1.5 py-0.5 text-[11px] font-semibold leading-none",
                      isActive ? "bg-indigo-50 text-indigo-600" : "bg-gray-100 text-gray-600",
                    )}
                  >
                    {docCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={() => handleCertificateDocumentUploadClick()}
          className="w-full shrink-0 cursor-pointer whitespace-nowrap rounded-md border-transparent bg-black px-4 py-2 text-xs font-medium text-white sm:w-auto"
        >
          Upload New Document
        </button>
      </div>
      <div className="mt-4">{isPrint ? <PrintCertificate /> : <UploadedDocuments />}</div>
    </div>
  );
};

export default CertificatesTab;
